// CLAUDE.md §3.3 · §12: sayı biçimleme YALNIZ buradan geçer.
// Binlik nokta, ondalık virgül; tarih/saat Europe/Istanbul.

export const SAAT_DILIMI = 'Europe/Istanbul'

/** Sayıyı tr-TR biçiminde döndürür. Boş değer boş kalır — sıfıra çevrilmez. */
export function tr(deger: number | null | undefined, basamak = 2): string {
  if (deger === null || deger === undefined || Number.isNaN(deger)) return '—'
  return deger.toLocaleString('tr-TR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: basamak,
  })
}

/**
 * Sayıyı TR biçimli bir metin GİRİŞİNE yazmak için hazırlar.
 *
 * `String(79.5)` "79.5" üretir; TR ayrıştırıcısı (`sayiyaCevir`) noktayı BİNLİK
 * ayırıcı sayıp bunu 795'e çevirir — form dolduğunda değer sessizce on katına
 * çıkar. Bu yüzden ondalık virgülle yazılır ve binlik ayırıcı HİÇ konulmaz:
 * gidiş dönüş kayıpsız olur.
 */
export function trGiris(deger: number | null | undefined): string {
  if (deger === null || deger === undefined || Number.isNaN(deger)) return ''
  return String(deger).replace('.', ',')
}

/** Sabit basamaklı biçim — tablo sütunlarında hizalama için. */
export function trSabit(deger: number | null | undefined, basamak = 2): string {
  if (deger === null || deger === undefined || Number.isNaN(deger)) return '—'
  return deger.toLocaleString('tr-TR', {
    minimumFractionDigits: basamak,
    maximumFractionDigits: basamak,
  })
}

export function trTarih(deger: string | Date | null | undefined): string {
  if (!deger) return '—'
  const d = typeof deger === 'string' ? new Date(deger) : deger
  return d.toLocaleDateString('tr-TR', { timeZone: SAAT_DILIMI })
}

export function trSaat(deger: string | Date | null | undefined): string {
  if (!deger) return '—'
  const d = typeof deger === 'string' ? new Date(deger) : deger
  return d.toLocaleTimeString('tr-TR', {
    timeZone: SAAT_DILIMI,
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function trTarihSaat(deger: string | Date | null | undefined): string {
  if (!deger) return '—'
  return `${trTarih(deger)} ${trSaat(deger)}`
}

/**
 * "Şu an" · "Bugün 07:58" · "Dün 19:24" · "11.05.2026 14:02".
 *
 * Ekran 13'ün oturum listesi ve "son giriş" satırı bu biçimi kullanıyor.
 * Gün karşılaştırması TESİS saat diliminde yapılıyor: UTC'ye göre yapılsaydı
 * gece yarısı civarındaki bir oturum "Dün" görünürdü.
 */
export function goreliZaman(deger: string | Date | null | undefined): string {
  if (!deger) return '—'
  const d = typeof deger === 'string' ? new Date(deger) : deger
  if (Number.isNaN(d.getTime())) return '—'

  const fark = Date.now() - d.getTime()
  if (fark >= 0 && fark < 2 * 60 * 1000) return 'Şu an'

  const gun = (x: Date) => x.toLocaleDateString('sv-SE', { timeZone: SAAT_DILIMI })
  const bugun = gun(new Date())
  const dun = gun(new Date(Date.now() - 86_400_000))
  const hedef = gun(d)

  if (hedef === bugun) return `Bugün ${trSaat(d)}`
  if (hedef === dun) return `Dün ${trSaat(d)}`
  return `${trTarih(d)} ${trSaat(d)}`
}

/**
 * Tarayıcı adını user-agent'tan çıkarır — oturum listesinin "Chrome · Windows"
 * satırı. Kesin bir bilim değil; tanınmayan UA olduğu gibi kısaltılıyor.
 */
export function cihazAdi(ua: string | null | undefined): string {
  if (!ua) return 'Bilinmeyen cihaz'

  const tarayici =
    /Edg\//.test(ua) ? 'Edge'
    : /OPR\/|Opera/.test(ua) ? 'Opera'
    : /Chrome\//.test(ua) ? 'Chrome'
    : /Firefox\//.test(ua) ? 'Firefox'
    : /Safari\//.test(ua) ? 'Safari'
    : null

  const isletim =
    /Windows/.test(ua) ? 'Windows'
    : /Android/.test(ua) ? 'Android'
    : /iPhone|iPad|iOS/.test(ua) ? 'iOS'
    : /Mac OS X/.test(ua) ? 'macOS'
    : /Linux/.test(ua) ? 'Linux'
    : null

  if (!tarayici && !isletim) return ua.length > 40 ? `${ua.slice(0, 40)}…` : ua
  return [tarayici, isletim].filter(Boolean).join(' · ')
}

/**
 * IP maskesi: ilk iki oktet görünür, kalanı gizli (88.243.••.••).
 * IPv6 ve ::1 için ilk iki grup gösteriliyor — biçim tanınmazsa olduğu gibi.
 */
export function ipMaskesi(ip: string | null | undefined): string {
  if (!ip) return '—'
  const v4 = ip.split('.')
  if (v4.length === 4) return `${v4[0]}.${v4[1]}.••.••`
  const v6 = ip.split(':').filter(Boolean)
  if (v6.length >= 2) return `${v6[0]}:${v6[1]}:••`
  return ip
}

/**
 * TR biçimli metni sayıya çevirir: binlik nokta atılır, ondalık virgül noktaya
 * döner. Geçersiz girdi 0 üretir — çağıranların çoğu "boş = 0" istiyor.
 *
 * Boşu NULL isteyenler için `sayiyaCevirBos` var: ayarlar ve lab formlarında
 * "girilmedi" ile "sıfır" farklı şeyler (K32).
 */
export function sayiyaCevir(metin: string): number {
  const x = parseFloat(metin.replace(/\./g, '').replace(',', '.'))
  return Number.isNaN(x) ? 0 : x
}

/** Boş ya da geçersiz girdide null — "girilmedi" ile "sıfır" ayrımı için. */
export function sayiyaCevirBos(metin: string): number | null {
  if (metin.trim() === '') return null
  const x = parseFloat(metin.replace(/\./g, '').replace(',', '.'))
  return Number.isNaN(x) ? null : x
}

// --------------------------------------------------------- tesis saati ↔ ISO

/**
 * Bir anın tesis saat diliminde kaç milisaniye ileride olduğu.
 *
 * IANA diliminden hesaplanıyor, sabit +03:00 yazılmıyor: Türkiye 2016'dan beri
 * kalıcı UTC+3 ama kural değişirse bu fonksiyon kendiliğinden doğru kalır.
 */
function tesisOfseti(an: Date): number {
  // 'sv-SE' + timeZone → "YYYY-MM-DD HH:mm:ss" (tesis duvar saati)
  const duvar = an.toLocaleString('sv-SE', { timeZone: SAAT_DILIMI })
  return Date.parse(`${duvar.replace(' ', 'T')}Z`) - an.getTime()
}

/**
 * Bugünün TESİS günü, `YYYY-MM-DD`.
 *
 * `new Date().toISOString().slice(0,10)` UTC gününü verir: gece 00:00–02:59
 * arasında bir gün GERİ döner. O saatte açılan sefer/bakım dünün tarihine
 * yazılıyor, belge numarası kayıyor ve kayıt bugünün listesinde görünmüyordu.
 */
export function trBugun(): string {
  return new Date().toLocaleDateString('sv-SE', { timeZone: SAAT_DILIMI })
}

/**
 * `datetime-local` girdisinin başlangıç değeri — tesis duvar saati,
 * `YYYY-MM-DDTHH:mm`. ISO'dan kesmek kullanıcıya 3 saat geriyi gösteriyordu.
 */
export function trYerelIso(an: Date = new Date()): string {
  return an.toLocaleString('sv-SE', { timeZone: SAAT_DILIMI }).replace(' ', 'T').slice(0, 16)
}

/**
 * `datetime-local` değerini (tesis duvar saati, ofssetsiz) doğru ana çevirir.
 *
 * `new Date(metin)` ofsetsiz metni TARAYICININ dilimine göre ayrıştırır;
 * tarayıcı başka bir dilimdeyse kayıt yanlış ana düşer.
 */
export function trIsoyaCevir(yerel: string): string {
  const utcVarsayimi = Date.parse(`${yerel.length === 16 ? `${yerel}:00` : yerel}Z`)
  if (Number.isNaN(utcVarsayimi)) return new Date().toISOString()
  return new Date(utcVarsayimi - tesisOfseti(new Date(utcVarsayimi))).toISOString()
}

/** Şu anki TESİS saati (0–23) — K30'un "NOW" karşılaştırmaları için. */
export function trSaatNo(): number {
  return Number(
    new Date().toLocaleString('en-GB', { timeZone: SAAT_DILIMI, hour: '2-digit', hour12: false }),
  )
}
